import { Domain, Error, JSONValue, Node as SchemaNode } from '@pitaman71/omniglot-introspect';
import faker from 'faker';

const __moduleName__ = 'omniglot-live-data.Domains.Values';

export interface Meta {
    identifier: string[],
    description: string
};

class JSONDomain extends Domain<JSONValue> {
    asProperties(): undefined { return undefined; }
    asSchema() { return 'any' }
    asJSON() {
        return {
            from(json: JSONValue) {
                return json;
            }, to(value: JSONValue|null) {
                return value;
            }
        }
    }
    asString() {
        return new class {
            from(text: string) { 
                if(text === '') return '';
                try {
                    return JSON.parse(text);
                } catch(err) {
                    return '';
                }
            }
            to(value: JSONValue) { 
                try {
                    return JSON.stringify(value) 
                } catch(err) {
                    return '';
                }
            }
        }
    }
    asEnumeration() {
        return new class {
            *forward(): Generator<boolean> {
                yield false;
                yield true;
            }
            *backward(): Generator<boolean> {
                yield true;
                yield false;
            }
        }
    }

    cmp(a: boolean, b:boolean) { return a < b ? -1 : a > b ? +1 : 0 }
}
export const TheJSONDomain = new JSONDomain();

class BooleanDomain extends Domain<boolean> {
    asProperties(): undefined { return undefined; }
    asSchema() { return 'boolean' }
    asJSON() {
        return {
            from(json: JSONValue, options?: { onError?: (error: Error) => void }) {
                if(typeof json === 'boolean') {
                    return true;
                } else if(typeof json === 'string') {
                    const lowercase = json.toLowerCase();
                    return lowercase === 'true' || lowercase === 't';
                }
                if(options?.onError) options.onError({ type: { expected: 'boolean' }})
                return null;
            }, to(value: boolean|null) {
                return value;
            }
        }
    }
    asString() {
        return new class {
            from(text: string, options?: { onError?: (error: Error) => void }) { 
                if(text.length > 0) {
                    if(text.toLowerCase() === 'true' || text.toLowerCase() === 'yes') return true;
                    if(text.toLowerCase() === 'false' || text.toLowerCase() === 'no') return false;
                }
                if(options?.onError)
                    options.onError({ format: { expected: { tokenType: 'true|false|yes|no' }}})
                return null;
        }
            to(value: boolean) { return value.toString() }
        }
    }
    asEnumeration() {
        return new class {
            *forward(): Generator<boolean> {
                yield false;
                yield true;
            }
            *backward(): Generator<boolean> {
                yield true;
                yield false;
            }
        }
    }

    cmp(a: boolean, b:boolean) { return a < b ? -1 : a > b ? +1 : 0 }
}
export const TheBooleanDomain = new BooleanDomain();

class NumberDomain extends Domain<number> {
    asProperties(): undefined { return undefined; }
    asSchema() { return 'number' }
    asJSON() {
        const domain = this;
        return {
            from(json: JSONValue, options?: { onError?: (error: Error) => void }) {
                if(typeof json === 'number') {
                    return json;
                } else if(typeof json === 'string') {
                    return domain.asString()?.from(json);
                }
                if(options?.onError) options.onError({ type: { expected: 'number' }})
                return null;
            }, to(value: number|null) {
                return value;
            }
        }
    }
    asString() {
        return {
            from(text: string, options?: { onError?: (error: Error) => void }): number|null { 
                if(text.length > 0) {
                    const result: number = +text;
                    if(Number.isNaN(result) && options?.onError)
                        options.onError({ format: { expected: { tokenType: 'number' }}});
                }
                if(text === '') return null;
                return +text;
            },
            to(value: number) { return value.toString() }
        }
    }

    cmp(a: number, b:number) { return a < b ? -1 : a > b ? +1 : 0 }
}
export const TheNumberDomain = new NumberDomain();

class StringDomain extends Domain<string> {
    asProperties(): undefined { return undefined }
    asSchema() { return 'string' }
    asJSON() {
        return {
            from(json: JSONValue, options?: { onError?: (error: Error) => void }) {
                if(typeof json === 'string') {
                    return json;
                }
                if(options?.onError) options.onError({ type: { expected: 'string' }})
                return null;
            }, to(value: string|null) {
                return value;
            }
        }
    }
    asString() {
        return new class {
            from(text: string) { 
                return text;
            }
            to(value: string) { return value }
        }
    }
    cmp(a: string, b:string) { return a < b ? -1 : a > b ? +1 : 0 }
}
export const TheStringDomain = new StringDomain();

export class RangeDomain extends Domain<number> {
    min: number|undefined;
    max: number|undefined;
    unit: number;
    span: number;
    
    constructor(min: number|undefined, max: number|undefined, unit: number) {
        super();
        this.min = min;
        this.max = max;
        this.unit = unit;
        this.span = min === undefined || max === undefined ? 0 : max - min;
    }

    asProperties(): undefined { return undefined; }
    asSchema() { return 'number' }
    asJSON() {
        const domain = this;
        return {
            from(json: JSONValue, options?: { onError?: (error: Error) => void }) {
                if(typeof json === 'number') {
                    return json;
                } else if(typeof json === 'string') {
                    return domain.asString().from(json, options);
                }
                if(options?.onError) options.onError({ type: { expected: 'number' }})
                return null;
            }, to(value: number|null) {
                return value;
            }
        }
    }
    asString() {
        const domain = this;
        return new class {
            from(text: string, options?: { onError?: (error: Error) => void }): number|null { 
                if(text.length > 0) {
                    const result: number = +text;
                    if(Number.isNaN(result) && options?.onError)
                        options.onError({ format: { expected: { tokenType: 'number' }}});
                    if(domain.min !== undefined && domain.min > result) {
                        if(options?.onError)
                            options.onError({ format: { expected: { tokenType: `number[${domain.min === undefined ? '-Infinity' : domain.min}:${domain.max === undefined ? '+Infinity' : domain.max}]`}}})
                        return null;
                    }
                }
                return null;
            }
            to(value: number) { return value.toString() }
        }
    }
    asEnumeration(maxCount: number) {
        if(this.min === undefined || this.max === undefined || this.span === undefined) return undefined;
        const min = this.min;
        const max = this.max;
        const unit = this.unit;
        if(Math.floor(this.span / this.unit) > maxCount) return undefined;
        return new class {
            *forward(): Generator<number> {
                let count = 0;
                for(let value = min; value < max; value += unit) {
                    yield value;
                    ++count;
                    if(count >= maxCount) return;
                }
            }
            *backward(): Generator<number> {
                let count = 0;
                for(let value = max; value <= min; value -= unit) {
                    yield value;
                    ++count;
                    if(count >= maxCount) return;
                }
            }
        }
    }

    cmp(a: number, b:number) { return a < b ? -1 : a > b ? +1 : 0 }
}

export function PickFromRange(domain: RangeDomain, ...history: number[]) { 
    if(domain.min === undefined || domain.max === undefined) return undefined;
    return ((Math.random() * (domain.max-domain.min)) + domain.min) % domain.unit; 
}

export class TagDomain extends Domain<string> {
    values: string[];
    constructor(...values: string[]) {
        super();
        this.values = values;
    }
    asProperties(): undefined { return undefined; }
    asSchema() { 
        return this.values.map(value => JSON.stringify(value)).join(' | ')+' | string';
    }
    asJSON() {
        const domain = this;
        return {
            from(json: JSONValue, options?: { onError?: (error: Error) => void }) {
                if(typeof json === 'string') {
                    return json;
                }
                if(options?.onError) options.onError({ type: { expected: 'string' }})
                return null;
            }, to(value: string|null) {
                return value;
            }
        }
    }
    asString() {
        return new class {
            from(text: string) { 
                return text;
            }
            to(value: string) { return value }
        }
    }
    asEnumeration(maxCount: number) {
        if(this.values.length > maxCount) return undefined;
        const target=this;
        return new class {
            *forward(): Generator<string> {
                for(let value of target.values) { yield value };
            }
            *backward(): Generator<string> {
                for(let value of target.values.reverse()) { yield value as string };
            }
        };
    }

    cmp(a: string, b:string) { return a < b ? -1 : a > b ? +1 : 0 }
}


export class EnumerationDomain<ValueType extends string> extends Domain<ValueType> {
    values: ValueType[];
    constructor(...values: ValueType[]) {
        super();
        this.values = values;
    }
    asProperties(): undefined { return undefined; }
    asSchema() { 
        return this.values.map(value => JSON.stringify(value)).join(' | ');
    }
    asJSON() {
        const domain = this;
        return {
            from(json: JSONValue, options?: { onError?: (error: Error) => void }) {
                if(typeof json === 'string' && domain.values.filter(value => value === json)) {
                    return json as ValueType;
                }
                if(options?.onError) options.onError({ type: { expected: 'string' }})
                return null;
            }, to(value: ValueType|null) {
                return value;
            }
        }
    }
    asString() {
        const target=this;
        const values = this.values;
        return new class {
            from(text: string, options?: { onError?: (error: Error) => void }) { 
                values.forEach(value => {
                    if(text === `${value}`) return value;
                });
                return null;
            }
            to(value: string) { return value as ValueType }
        }
    }
    asEnumeration(maxCount: number) {
        if(this.values.length > maxCount) return undefined;
        const target=this;
        return new class {
            *forward(): Generator<ValueType> {
                for(let value of target.values) { yield value as ValueType};
            }
            *backward(): Generator<ValueType> {
                for(let value of target.values.reverse()) { yield value as ValueType};
            }
        };
    }

    cmp(a: ValueType, b:ValueType) { return a < b ? -1 : a > b ? +1 : 0 }
}

export class OptionalDomain<NullType extends null|undefined, ElementType> extends Domain<ElementType|NullType> {
    preferred: NullType;
    alternate: NullType[]

    elementDomain: Domain<ElementType>;
    constructor(elementDomain: Domain<ElementType>, preferred: NullType, ...alternate: NullType[]) {
        super();
        this.elementDomain = elementDomain;
        this.preferred = preferred;
        this.alternate = alternate;
    }
    _test(the: ElementType|NullType): boolean {
        if(the === this.preferred || this.alternate.filter(_value => _value === the).length > 0) return false;
        return true;
    }

    element() { 
        return this.elementDomain;
    }
    asProperties(): undefined { return undefined; }
    asSchema() { 
        const elementSchema = this.elementDomain.asSchema() || 'any';
        return [ elementSchema, [ this.preferred, ...this.alternate ].map(code => `${code}`)].join(' | ');
    }
    asJSON() {
        const domain = this;
        return {
            from(json: JSONValue, options?: { onError?: (error: Error) => void }) {
                if(json === null) return domain.preferred;
                const elementAsJSON = domain.elementDomain.asJSON();
                if(!elementAsJSON) return domain.preferred;
                return elementAsJSON.from(json, options);
            }, to(value: ElementType|NullType) {
                if(!domain._test(value)) return null;
                const elementAsJSON = domain.elementDomain.asJSON();
                if(!elementAsJSON) return null;
                return elementAsJSON.to(value as ElementType);
            }
        }
    }
    asString() {
        const domain = this;
        return {
            from(text: string) { 
                if(text === '') return domain.preferred;
                const elementAsString = domain.elementDomain.asString();
                if(!elementAsString) return domain.preferred;
                return elementAsString.from(text);
            },
            to(value: ElementType|NullType) { 
                if(!domain._test(value)) return '';
                const elementAsString = domain.elementDomain.asString();
                if(!elementAsString) return '';
                return elementAsString.to(value as ElementType);
            }
        }
    }

    cmp(a: ElementType|NullType, b: ElementType|NullType) { 
        const aTest = this._test(a);
        const bTest = this._test(b);
        if(!aTest && bTest) return -1;
        if(aTest && !bTest) return +1;
        if(!aTest && !bTest) return 0;
        return this.elementDomain.cmp(a as ElementType,b as ElementType);
    }
}

export class ArrayDomain<ElementType> extends Domain<Array<ElementType>> {
    elementDomain: Domain<ElementType>;
    constructor(elementDomain: Domain<ElementType>) {
        super();
        this.elementDomain = elementDomain;
    }

    element() { 
        return this.elementDomain;
    }

    asProperties(): undefined { return undefined; }
    asSchema() { 
        const elementSchema = this.elementDomain.asSchema() || 'any';
        return [ elementSchema ];
    }
    asJSON() {
        const domain = this;
        return {
            from(json: JSONValue, options?: { onError?: (error: Error) => void }) {
                if(!Array.isArray(json)) {
                    if(options?.onError) options.onError({ type: { expected: 'array' }})
                    return null;
                }
                const elementAsJSON = domain.elementDomain.asJSON();
                return json.map(item => elementAsJSON ? elementAsJSON.from(item) : item as ElementType).filter(item => !!item) as Array<ElementType>;
            }, to(value: Array<ElementType>|null) {
                if(value === null) return null;
                const elementAsJSON = domain.elementDomain.asJSON();
                return value.map(item => elementAsJSON ? elementAsJSON.to(item) : item as JSONValue);
            }
        }
    }
    asString() {
        const domain = this;
        return {
            from(text: string) { 
                return domain.asJSON()?.from(text) || null;
            },
            to(value: Array<ElementType>): string { 
                if(!Array.isArray(value)) return '';
                const asJSON = domain.asJSON();
                return JSON.stringify(!!asJSON ? asJSON.to(value) : value);
            }
        }
    }

    cmp(a: Array<ElementType>, b:Array<ElementType>) { 
        if(a.length < b.length) return -1;
        if(a.length > b.length) return +1;
        for(let index = 0;index < a.length;++index) {
            const code = this.elementDomain.cmp(a[index], b[index]);
            if(code !== 0) return code;
        }
        return 0;
    }
}

export class AggregateDomain<PropTypes extends {
    [propName:string]: any
}> extends Domain<Partial<PropTypes>> {
    properties: { [propName in keyof PropTypes]: Domain<PropTypes[propName]> };
    optionals: (keyof PropTypes)[];
    constructor(properties: { [propName in keyof PropTypes]: Domain<PropTypes[propName]> }, optionals?: (keyof PropTypes)[]) {
        super();
        this.properties = properties;
        this.optionals = optionals || [];
    }

    asProperties() {
        const names = Object.keys(this.properties) as any;
        return {
            names,
            domain: (propName: string) => this.properties[propName]
        };
    }

    asSchema() { 
        const propSchemas = Object.entries<Domain<PropTypes[keyof PropTypes]>>(this.properties).reduce<SchemaNode<any>>( (result, [propName, property]) => {
            return { ...result, [propName]: property.asSchema() || 'any' };
        }, {});
        return propSchemas
    }

    asJSON() {
        const domain = this;
        return {
            from(json: JSONValue, options?: { onError?: (error: Error) => void }): null|Partial<PropTypes> {
                if(json === null) return null;
                if(typeof json !== 'object' || Array.isArray(json)) {
                    if(options?.onError) options.onError({ type: { expected: 'object' }})
                    return null;
                }
                let errors: Error = { properties: {} };
                const result = Object.entries<Domain<PropTypes[keyof PropTypes]>>(domain.properties).reduce<Partial<PropTypes>>( (result, [propName, property]) => {
                    const asJSON = property.asJSON();
                    if(json[propName] === undefined) return result;
                    return { ...result, [propName]: !asJSON ? json[propName] : asJSON.from(json[propName] || null, { onError: error_ => {
                        errors.properties = { ...errors.properties, [propName]: { status: 'malformed', details: error_ }};
                    }})};
                }, {});
                const missingProperties = Object.entries<Domain<PropTypes[keyof PropTypes]>>(domain.properties).reduce<string[]>( (missingProperties, [propName, property]) => (domain.optionals.includes(propName)) ? missingProperties : (propName in json) ? missingProperties : [...missingProperties, propName], []);
                if(missingProperties.length > 0) {
                    const error = missingProperties.reduce<Error>((_errors, propName) => {
                        if(propName in (_errors.properties || {})) { 
                            return _errors;
                        } else {
                            return {
                                properties: {
                                    ...errors.properties,
                                    [propName]: { status: 'missing' }
                                }
                            }
                        }                        
                    }, errors);
                    if(options?.onError) options.onError(error)
                    /* return null; */
                }
                return result as Partial<PropTypes>;
            }, to(value: Partial<PropTypes>|null) {
                if(value === null) return null;
                return Object.entries<Domain<PropTypes[keyof PropTypes]>>(domain.properties).reduce( (result, [propName, property]) => {
                    const asJSON = property.asJSON();
                    return { ...result, [propName]: !asJSON ? value[propName as keyof PropTypes] : asJSON.to(value[propName as keyof PropTypes] || null)}
                }, {});
            }
        }
    }
    asString() {
        const domain = this;
        return {
            from(text: string) { 
                return domain.asJSON()?.from(text) || null;
            },
            to(value: Partial<PropTypes>) { 
                if(value === null) return '';
                const asJSON = domain.asJSON();
                return JSON.stringify(!!asJSON ? asJSON.to(value) : value);
            }
        }
    }

    cmp(a: Partial<PropTypes>, b: Partial<PropTypes>) {
        return Object.entries<Domain<PropTypes[keyof PropTypes]>>(this.properties).reduce<-1|1|0|undefined>( (result, [propName, property]) => {
            if(result !== 0) return result;
            const aHas = propName in a ? a[propName as keyof PropTypes] : undefined;
            const bHas = propName in b ? b[propName as keyof PropTypes] : undefined;
            if(!aHas && bHas) return -1;
            else if(aHas && !bHas) return +1;
            else if(aHas && bHas) {
                return property.cmp(aHas, bHas);
            }
            return 0;
        }, 0);
    }
}

export function PickWords(
    min: number,
    max: number
 ) {
    const count = Math.floor(Math.random() * (max - min)) + min;
    return faker.lorem.words(count);
}

export function PickSentences(
    min: number,
    max: number
 ) {
    const count = Math.floor(Math.random() * (max - min)) + min;
    return faker.lorem.sentences(count);
}

export function PickParagraphs(
    min: number,
    max: number
 ) {
    const count = Math.floor(Math.random() * (max - min)) + min;
    return faker.lorem.paragraphs(count);
}

export const Count = new RangeDomain(0, undefined, 1);

export const Fraction = new RangeDomain(0.0, 1.0, 0.00000001);

export interface Parseable<ElementType>  {
    text?: JSONValue,
    parsed?: ElementType,
    error?: JSONValue,
    suggestion?: JSONValue
};

export class ParseableDomain<ElementType> extends AggregateDomain<Parseable<ElementType>> {
    elementDomain: Domain<ElementType>;
    constructor(elementDomain: Domain<ElementType>) {
        super({
            text: TheJSONDomain,
            parsed: elementDomain,
            error: TheJSONDomain,
            suggestion: TheJSONDomain
        }, [ 'text', 'parsed', 'error', 'suggestion']);
        this.elementDomain = elementDomain;
    }
    asJSON() {
        const superFromJSON = (json: JSONValue) => super.asJSON().from(json);
        const asJSON = this.elementDomain.asJSON();
        const asString = this.elementDomain.asString()
        return {
            from(json: JSONValue, options?: { onError?: (error: Error) => void }): Partial<Parseable<ElementType>>|null {
                if(!!json && typeof json === 'object' && !Array.isArray(json) && Object.keys(json).includes('parsed')) {
                    return superFromJSON(json);
                }
                const parsed = asJSON?.from(json, options);
                if(!parsed) return null;
                return {
                    parsed,
                    text: typeof json === 'string' ? json : asString?.to(parsed)
                };
            }, to(value: Partial<Parseable<ElementType>>): JSONValue {
                if(value === null) return null;
                return asJSON?.to(value.parsed || null) || null;
            }
        }
    }
}
